import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight } from "lucide-react";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import useSeo from "@/hooks/useSeo";
import { BLOG_POSTS } from "@/data/seo/blog";
import { breadcrumbSchema, canonicalFor } from "@/lib/seoHelpers";

export default function BlogIndex() {
  const canonical = canonicalFor("/blog");
  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Blog", href: "/blog" },
  ];

  useSeo({
    title:
      "The InvitationWala Journal · Wedding Invitations, Trends & Etiquette",
    description:
      "Evergreen guides on wedding invitations, wedding websites, luxury wedding trends, RSVP etiquette and Indian wedding traditions.",
    canonical,
  });

  const schemas = [breadcrumbSchema(crumbs)];

  return (
    <SeoPageLayout schemas={schemas}>
      <div
        data-testid="blog-index"
        className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-14 md:mb-20">
          <span className="overline text-[var(--iw-gold)]">The Journal</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Wedding Invitations, Trends & Etiquette
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mt-8 max-w-2xl">
            Evergreen guides on wedding invitations, RSVP etiquette, luxury
            wedding trends and Indian wedding traditions. Written by our design
            atelier for couples planning ambitious, considered weddings.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block"
              data-testid={`blog-card-${post.slug}`}
            >
              <div
                className="relative aspect-[16/10] overflow-hidden mb-5"
                style={{ border: "1px solid rgba(212,175,55,0.2)" }}
              >
                <img
                  src={post.heroImage}
                  alt={`${post.title} — InvitationWala journal`}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  loading="lazy"
                  decoding="async"
                />
                <span className="absolute top-3 left-3 overline text-[var(--iw-gold)] bg-[rgba(20,15,15,0.85)] px-3 py-1.5">
                  {post.category}
                </span>
              </div>
              <div className="flex items-center gap-3 overline text-[var(--iw-faint)] mb-2 text-[10px]">
                <span className="flex items-center gap-1.5">
                  <Clock size={9} className="text-[var(--iw-gold)]" />
                  {post.readTime}
                </span>
                <span>·</span>
                <span>
                  {new Date(post.datePublished).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <h2
                className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-tight group-hover:text-[var(--iw-gold)] transition-colors flex items-start gap-2"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                <span>{post.title}</span>
                <ArrowUpRight
                  size={16}
                  className="text-[var(--iw-faint)] group-hover:text-[var(--iw-gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-1"
                />
              </h2>
              <p className="text-[var(--iw-muted)] text-sm leading-relaxed mt-2 line-clamp-3">
                {post.excerpt}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </SeoPageLayout>
  );
}
